import { Loader2, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminIntegrationVisibilityRow, type AdminOrgAssignedIntegration } from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadError } from '@/components/ui/load-state';
import { formatDate } from '@/lib/format';
import { assignableIntegrations, integrationKindLabel, integrationPath } from '@/lib/integrationVisibility';
import { useOrgScope } from '@/pages/org/OrgLayout';

import { IntegrationLogo } from '../IntegrationBuilder/builderUi';

function message(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function IntegrationName({ row }: { row: AdminIntegrationVisibilityRow }) {
  return (
    <Link to={integrationPath(row.integrationId)} className="flex min-w-0 items-center gap-3">
      <IntegrationLogo src={row.logoUrl} color={row.brandColor ?? '#64748b'} title={row.title} size="sm" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">{row.title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {row.integrationId} · {integrationKindLabel(row.kind)}
        </span>
      </span>
    </Link>
  );
}

/**
 * Studio Admin → an organization → Integrations: the integrations assigned to it, which it can find and use
 * (on unless it turns them off). Integrations open to everyone are not listed: every organization has them.
 */
export function OrgIntegrationsPage() {
  const { orgId } = useOrgScope();
  const [assigned, setAssigned] = useState<AdminOrgAssignedIntegration[]>();
  const [rows, setRows] = useState<AdminIntegrationVisibilityRow[]>([]);
  const [error, setError] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setAssigned(undefined);
    setError(undefined);
    Promise.all([adminClient.listOrgAssignedIntegrations(orgId), adminClient.listIntegrationVisibility()])
      .then(([mine, all]) => {
        if (cancelled) return;
        setAssigned(mine.items);
        setRows(all.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(message(err, 'Failed to load the integrations'));
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, reloadKey]);

  const change = useCallback(
    async (apply: () => Promise<unknown>, success: string, fallback: string) => {
      setBusy(true);
      setActionError(undefined);
      try {
        await apply();
        setAssigned((await adminClient.listOrgAssignedIntegrations(orgId)).items);
        toast.success(success);
      } catch (err) {
        setActionError(message(err, fallback));
      } finally {
        setBusy(false);
      }
    },
    [orgId],
  );

  const needle = query.trim().toLowerCase();
  const available = assignableIntegrations(rows, (assigned ?? []).map((row) => row.integrationId)).filter(
    (row) => !needle || `${row.title} ${row.integrationId}`.toLowerCase().includes(needle),
  );

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Integrations"
        description="Integrations offered only to the organizations RapidSOS assigns them to. This organization can find and use the ones listed here; each is on unless it turns it off. Integrations open to everyone are not listed, because every organization has them."
      />
      {error ? (
        <LoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : !assigned ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the integrations
        </div>
      ) : (
        <div className="space-y-6">
          {actionError ? (
            <p role="alert" className="text-sm text-destructive">
              {actionError}
            </p>
          ) : null}
          <AdminSection title="Assigned to this organization">
            {assigned.length === 0 ? (
              <p className="text-sm text-muted-foreground">No integration is assigned to this organization.</p>
            ) : (
              <ul aria-label="Assigned integrations" className="divide-y divide-border rounded-lg border border-border">
                {assigned.map((row) => (
                  <li key={row.integrationId} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 space-y-0.5">
                      <IntegrationName row={row} />
                      <span className="block text-xs text-muted-foreground">
                        {row.visibility === 'everyone' ? 'Open to everyone now, so every organization has it. ' : ''}
                        {row.assignedBy ? `Added by ${row.assignedBy}` : 'Added'}
                        {row.assignedAt ? ` on ${formatDate(row.assignedAt)}` : ''}
                      </span>
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      aria-label={`Remove ${row.title}`}
                      onClick={() =>
                        void change(
                          () => adminClient.unassignIntegration(row.integrationId, orgId),
                          `${row.title} removed`,
                          'Failed to remove the integration',
                        )
                      }
                    >
                      <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </AdminSection>

          <AdminSection title="Add integration" description="Integrations offered to specific organizations that this one does not have yet.">
            <div className="space-y-3">
              <Input
                aria-label="Search integrations to add"
                placeholder="Search name or integration id"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
                disabled={busy}
              />
              {available.length > 0 ? (
                <ul aria-label="Integrations to add" className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                  {available.map((row) => (
                    <li key={row.integrationId} className="flex items-center justify-between gap-3 px-3 py-2">
                      <IntegrationName row={row} />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        aria-label={`Add ${row.title}`}
                        onClick={() =>
                          void change(
                            () => adminClient.assignIntegration(row.integrationId, orgId),
                            `${row.title} added`,
                            'Failed to add the integration',
                          ).then(() => setQuery(''))
                        }
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {needle ? `No integration to add matches “${query.trim()}”.` : 'Every integration offered to specific organizations is already here.'}
                </p>
              )}
            </div>
          </AdminSection>
        </div>
      )}
    </div>
  );
}
